import { SHStack, SHTypography } from "@components/design-systems";
import { ManagedAccountFeeAnalysisContainerDTO } from "@models/managed-accounts/entities/step/fee";
import { AnalysisVirtualizedStepProps } from "@models/platform-analysis/entities/analysis";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { useTheme } from "@mui/material";
import { AnalysisVirtualizedTableStepConfigs } from "@pages/platform-analysis-v2/_id/configs";
import { RootState } from "@redux/store";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { FeeAnalysisTable } from "./table";
import { PortfolioFeesByPlatformTableToolbar } from "./toolbar";

const TABLE_VIRTUALIZE_PROPS =
  AnalysisVirtualizedTableStepConfigs[AnalysisStep.Fee];

interface FeeSMAPortfolioSectionProps {
  isAppendix?: boolean;
  displayControls?: boolean;
  columnSize?: "small" | "large";
  backgroundColor?: string;
  padding?: string;
  border?: string;
  virtualizeProps?: AnalysisVirtualizedStepProps;
  disabled?: boolean;
  showControls?: boolean;
  getTableDataFunc?: (
    state: RootState,
  ) => ManagedAccountFeeAnalysisContainerDTO | undefined;
  getHiddenIdsIndexMap?: (state: RootState) => boolean[] | undefined;
}

export const FeeSMAPortfolioSection = memo(
  ({
    isAppendix = false,
    backgroundColor,
    padding,
    border,
    virtualizeProps = TABLE_VIRTUALIZE_PROPS,
    disabled,
    showControls = true,
    getHiddenIdsIndexMap = (state: RootState) =>
      state.platformAnalysis?.internal?.feeAnalysis?.hiddenIdsIndexMap,
  }: FeeSMAPortfolioSectionProps) => {
    const { palette } = useTheme();

    const storedHiddenSubProductIndexMap = useSelector(getHiddenIdsIndexMap);
    const storedPerformanceScope = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.feeAnalysis?.performanceScope,
    );
    const storedDisplayMode = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.feeAnalysis?.displayMode,
    );
    const storedDisplayStyle = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.internal?.feeAnalysis?.displayStyle,
    );

    const numberOfHiddenSubProducts = useMemo(
      () =>
        storedHiddenSubProductIndexMap?.reduce(
          (sum, isHidden) => sum + (isHidden ? 1 : 0),
          0,
        ) ?? 0,
      [storedHiddenSubProductIndexMap],
    );

    const isEmptyData = useMemo(
      () => !!storedHiddenSubProductIndexMap?.every(Boolean),
      [storedHiddenSubProductIndexMap],
    );

    const tableTitle = useMemo(
      () =>
        `Total fees and costs ${
          storedPerformanceScope === FeesPerformanceScope.Including
            ? "including"
            : "excluding"
        } performance fees (p.a.)`,
      [storedPerformanceScope],
    );

    return (
      <SHStack
        width={"100%"}
        bgcolor={backgroundColor}
        padding={padding}
        border={border}
      >
        <PortfolioFeesByPlatformTableToolbar
          isAppendix={isAppendix}
          showControls={showControls}
          displayStyle={storedDisplayStyle}
          displayMode={storedDisplayMode}
          performanceScope={storedPerformanceScope}
          disabled={disabled}
        />

        {isEmptyData || !virtualizeProps ? (
          <SHStack>
            <SHTypography variant="subtitle1" colorVariant="disabled">
              No data
            </SHTypography>
            <SHTypography variant="body2" colorVariant="disabled">
              No products match the criteria in your filter
            </SHTypography>
          </SHStack>
        ) : (
          <FeeAnalysisTable
            tableRenderConfig={virtualizeProps}
            tableTitle={tableTitle}
          />
        )}

        <SHStack marginTop={3}>
          <SHTypography variant={"subtitle2"} color={palette.primary.main}>
            {`${numberOfHiddenSubProducts} ${
              numberOfHiddenSubProducts === 1 ? "product" : "products"
            } hidden`}
          </SHTypography>
        </SHStack>
      </SHStack>
    );
  },
);
